<template>
  <header
      class="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 px-4 flex items-center justify-between z-50">
    <button @click="isMobileOpen = !isMobileOpen" class="p-2 rounded-lg bg-gray-50 text-[#163271]">
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
      </svg>
    </button>

    <img class="h-8 w-auto object-contain" src="/images/icons/logo_r.webp" alt="Cazalec_Logo">
  </header>

  <div v-if="isMobileOpen" @click="isMobileOpen = false"
       class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden"></div>

  <aside
      :class="[
      'fixed inset-y-0 left-0 z-50 w-64 bg-white flex flex-col border-r border-gray-100 transition-transform duration-300 transform lg:translate-x-0 lg:static lg:h-screen',
      isMobileOpen ? 'translate-x-0' : '-translate-x-full'
    ]"
  >
    <div class="relative p-6 pb-2 lg:pt-6 pt-8">
      <button
          v-if="isMobileOpen"
          @click="isMobileOpen = false"
          class="lg:hidden absolute top-4 right-4 p-1.5 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>

      <div class="flex items-center p-4 rounded-2xl bg-gradient-to-r from-slate-50 to-gray-50/50 border border-gray-100 shadow-2xs mt-8  relative overflow-hidden group">
        <div class="absolute left-0 top-0 bottom-0 w-1 rounded-r-lg" :class="roleBarClass"></div>

        <div class="flex-shrink-0 h-12 w-12 rounded-xl bg-white border border-gray-200/60 shadow-3xs text-[#163271] flex items-center justify-center transition-transform group-hover:scale-105 duration-200">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          </svg>
        </div>

        <div class="ml-3.5 min-w-0 flex-1 flex flex-col justify-center">


          <h4 class="text-sm font-extrabold text-slate-800 truncate leading-snug">
            {{ formatName(user?.fullName) }}
          </h4>
          <span class="text-[9px] font-black uppercase tracking-widest py-0.5 rounded-md bg-white  w-max inline-flex items-center" :class="roleClass">
            <span class="h-1.5 w-1.5 rounded-full mr-1.5 animate-pulse" :class="roleDotClass"></span>
            {{ roleLabel }}
          </span>
          <p class="text-[11px] text-slate-400 font-medium truncate mt-0.5" :title="user?.email">
            {{ user?.email || 'adresse@cazalec.fr' }}
          </p>
        </div>
      </div>
    </div>

    <nav class="flex-1 overflow-y-auto px-4 space-y-1 custom-scrollbar pt-2">

      <!-- ========================================== -->
      <!-- 1. Section: GESTION (Admin & Supervisor)   -->
      <!-- ========================================== -->
      <template v-if="isAdmin || isSupervisor">
        <div class="pt-2 pb-1 px-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">Management</div>

        <router-link to="/admin/dashboard" class="nav-link" active-class="active-link">
          <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg> Tableau de bord
        </router-link>

        <router-link to="/admin/prospects" class="nav-link" active-class="active-link">
          <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
          </svg> Prospects
        </router-link>

        <router-link to="/admin/clients/archive" class="nav-link" active-class="active-link">
          <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>
          </svg> Archives
        </router-link>

        <!-- قسم العقود المشترك -->
        <div class="pt-4 pb-1 px-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">Contrats</div>

        <router-link to="/admin/pending-contracts" class="nav-link" active-class="active-link">
          <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/>
          </svg> En cours
        </router-link>

        <!-- يظهر فقط للأدمن -->
        <router-link v-if="isAdmin" to="/admin/active-clients" class="nav-link" active-class="active-link">
          <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
          </svg> Signés (Rétention)
        </router-link>

        <!-- ========================================== -->
        <!-- 2. Section: TERRAIN (Supervisor & Worker) -->
        <!-- ========================================== -->
        <template v-if="isSupervisor || isWorker">
          <div class="pt-4 pb-1 px-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">Espace Terrain</div>

          <router-link to="/worker/dashboard" class="nav-link" active-class="active-link">
            <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg> Radar de performance
          </router-link>

          <router-link to="/worker/workspace" class="nav-link" active-class="active-link">
            <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
            </svg> Workspace
          </router-link>

          <router-link to="/worker/my-contracts" class="nav-link" active-class="active-link">
            <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg> Mes Dossiers
          </router-link>
        </template>

        <!-- ========================================== -->
        <!-- 3. Section: CONFIG (Admin & Supervisor)    -->
        <!-- ========================================== -->
        <div class="pt-4 pb-1 px-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">Configuration</div>

        <router-link to="/admin/users" class="nav-link" active-class="active-link">
          <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg> Employés
        </router-link>

        <router-link v-if="isAdmin" to="/admin/settings" class="nav-link" active-class="active-link">
          <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg> Paramètres & API
        </router-link>
      </template>

      <!-- ========================================== -->
      <!-- 4. Version Simple pour Worker uniquement   -->
      <!-- ========================================== -->
      <template v-else-if="isWorker">
        <div class="pt-2 pb-1 px-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">Ma Performance</div>

        <router-link to="/worker/dashboard" class="nav-link" active-class="active-link">
          <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg> Tableau de bord
        </router-link>

        <div class="pt-4 pb-1 px-3 text-[10px] font-black text-gray-400 uppercase tracking-wider">Mes Opérations</div>

        <router-link to="/worker/workspace" class="nav-link" active-class="active-link">
          <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
          </svg> Workspace
        </router-link>

        <router-link to="/worker/my-contracts" class="nav-link" active-class="active-link">
          <svg xmlns="http://www.w3.org/2000/svg" class="mr-3 inline-block w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg> Mes Dossiers
        </router-link>
      </template>

    </nav>

    <!-- Bottom Section -->
    <div class="p-4 border-t border-gray-50 space-y-2">
      <router-link
          :to="isAdmin || isSupervisor ? '/admin/profile' : '/worker/profile'"
          class="flex items-center w-full px-4 py-3 rounded-xl bg-slate-50 text-[#163271] hover:bg-[#22b5af] hover:text-white transition-all duration-300 font-bold"
      >
        <i class="bi bi-person-circle mr-3"></i> Mon Profil
      </router-link>

      <button @click="logout" class="flex items-center w-full px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 transition-all duration-200 font-bold">
        <i class="bi bi-box-arrow-right mr-3"></i> Déconnexion
      </button>
    </div>


  </aside>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { jwtDecode } from "jwt-decode";

import apiClient from "@/api/index.js";
const route = useRoute();
const router = useRouter();
const isMobileOpen = ref(false);
const user = ref(null);

// جلب الأدوار من التوكن لمرة واحدة
const roles = computed(() => {
  const token = localStorage.getItem('token');
  if (!token) return [];
  try {
    const decoded = jwtDecode(token);
    return decoded.roles || [];
  } catch (e) {
    return [];
  }
});

// جلب اسم الصلاحية بالفرنسية بناءً على الدور الحالي
const roleLabel = computed(() => {
  if (isAdmin.value) return 'Administrateur';
  if (isSupervisor.value) return 'Superviseur';
  if (isWorker.value) return 'Employé';
  return 'Utilisateur';
});

// دالة لتحويل الحرف الأول من كل كلمة إلى Majuscule
const formatName = (name) => {
  if (!name) return 'Chargement...';
  return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
};

// الألوان المخصصة لشارة النص
const roleClass = computed(() => {
  if (isAdmin.value) return 'text-red-600 bg-red-50/40 border-red-100/70';
  if (isSupervisor.value) return 'text-blue-600 bg-blue-50/40 border-blue-100/70';
  return 'text-slate-500 bg-slate-50 border-gray-200/60';
});

// لون النقطة النابضة (Dot) داخل الشارة
const roleDotClass = computed(() => {
  if (isAdmin.value) return 'bg-red-500';
  if (isSupervisor.value) return 'bg-blue-500';
  return 'bg-slate-400';
});

// لون الخط الجانبي الأنيق للبطاقة
const roleBarClass = computed(() => {
  if (isAdmin.value) return 'bg-red-500';
  if (isSupervisor.value) return 'bg-blue-500';
  return 'bg-[#22b5af]'; // لمسة من اللون السكندري للمشروع عند الموظف
});

// تعريف الصلاحيات بشكل نظيف
const isAdmin = computed(() => roles.value.includes('ROLE_ADMIN'));
const isSupervisor = computed(() => roles.value.includes('ROLE_SUPERVISOR'));

// العامل هو من لديه الدور ROLE_WORKER ولا يملك دور أدمن أو مشرف
const isWorker = computed(() =>
    roles.value.includes('ROLE_WORKER') && !isAdmin.value && !isSupervisor.value
);
const fetchProfile = async () => {
  try {
    const response = await apiClient.get('/user/me');
    user.value = response.data;
  } catch (e) { console.error("Erreur de chargement du profil", e); }
};

const logout = () => {
  localStorage.removeItem('token');
  router.push('/login');
};
watch(() => route.path, () => {
  isMobileOpen.value = false;
});
// تشغيل جلب البيانات تلقائياً عند بناء المكون
onMounted(() => {
  fetchProfile();
});
</script>

<style scoped>
/* تصميم الروابط الاحترافي */
.nav-link {
  @apply flex items-center py-2.5 px-4 rounded-xl transition-all duration-200 font-bold text-slate-500 hover:bg-slate-50 hover:text-[#163271] text-sm;
}

/* الرابط النشط */
.active-link {
  background-color: #163271 !important;
  color: white !important;
  box-shadow: 0 4px 20px rgba(22, 50, 113, 0.15);
}

/* تنسيق السكرول بار لجعلها مخفية وجميلة */
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #f5f5f5;
  border-radius: 10px;
}
</style>